/**
 * Administration → Storage. Sub-views (1.1.0, ?view=, as the model editor has): Locations (the default: tiers,
 * locations and the integrity check), Tiering rules (?view=rules), Migrations (?view=migrations, administrators) and
 * Transfer windows (?view=windows). 1.2.3: Old versions (?view=old-versions), for Delete old versions with Manage
 * storage.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Save } from 'lucide-react';
import type { StoragePolicyDTO, StoragePurpose, StorageRole } from '@prism/contracts';
import { api, errorDetail } from '../../api/client';
import { useMe, useStorage } from '../../api/hooks';
import { Segmented } from '../../components/Segmented';
import { toast } from '../../components/Toasts';
import { relative } from '../../lib/format';
import { TieringRules } from './TieringRules';
import { AddLocationDialog } from './storage/AddLocationDialog';
import { LocationCard } from './storage/LocationCard';
import { IntegrityCheck } from './storage/IntegrityCheck';
import { Migrations } from './storage/Migrations';
import { TransferWindows } from './storage/TransferWindows';
import { OldVersions } from './storage/OldVersions';
import { queryKeys } from '../../api/queryKeys';

type StorageView = 'locations' | 'rules' | 'migrations' | 'windows' | 'old-versions';


const TIERS: Array<{ key: keyof Pick<StoragePolicyDTO, 'originalsLocationId' | 'derivativesLocationId' | 'collateralLocationId' | 'exportsLocationId'>; role: StorageRole; label: string; help: string }> = [
  { key: 'originalsLocationId', role: 'originals', label: 'Hi-res originals', help: 'Originals of assets. Can be PRISM-managed storage on a storage agent.' },
  { key: 'derivativesLocationId', role: 'derivatives', label: 'Proxies & previews', help: 'Proxies, thumbnails, scrub images and waveforms; must stream to browsers.' },
  { key: 'collateralLocationId', role: 'collateral', label: 'Collateral', help: 'Subtitles, graphics, manuscripts and other attachments' },
  { key: 'exportsLocationId', role: 'exports', label: 'Exports', help: 'Where exports are rendered and played from unless an export location is chosen (Export locations tab).' },
];

export function StorageAdmin() {
  const perms = useMe().data?.permissions ?? [];
  const isAdmin = perms.includes('admin');
  // Old versions (1.2.3): Delete old versions together with Manage storage (or an administrator).
  const oldVersions = perms.includes('asset.delete_versions') && (perms.includes('storage.manage') || isAdmin);
  const [params, setParams] = useSearchParams();
  const asked = params.get('view');
  const view: StorageView = asked === 'rules' || asked === 'windows' || (asked === 'migrations' && isAdmin) || (asked === 'old-versions' && oldVersions) ? asked : 'locations';
  const q = useStorage();
  const items = q.data?.items ?? [];
  const tabs = (
    <Segmented role="tablist" label="Storage" buttonRole="tab" buttonType="button" value={view} onChange={(v) => setParams(v === 'locations' ? {} : { view: v })} options={[
      { value: 'locations', label: 'Locations', testId: 'storage-view-locations' },
      { value: 'rules', label: 'Tiering rules', testId: 'storage-view-rules' },
      ...(isAdmin ? [{ value: 'migrations' as const, label: 'Migrations', testId: 'storage-view-migrations' }] : []),
      { value: 'windows', label: 'Transfer windows', testId: 'storage-view-windows' },
      ...(oldVersions ? [{ value: 'old-versions' as const, label: 'Old versions', testId: 'storage-view-old-versions' }] : []),
    ]} />
  );
  return (
    <div className="page">
      {tabs}
      <div style={{ height: 16 }} />
      {view === 'locations' && <StorageLocations />}
      {view === 'rules' && <TieringRules locations={items} />}
      {view === 'migrations' && <Migrations locations={items} />}
      {view === 'windows' && <TransferWindows />}
      {view === 'old-versions' && <OldVersions locations={items} />}
    </div>
  );
}

/** Locations: the storage tiers, the locations and the integrity check. */
function StorageLocations() {
  const q = useStorage();
  const qc = useQueryClient();
  const items = q.data?.items ?? [];
  const [policy, setPolicy] = useState<StoragePolicyDTO | null>(null);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState<StoragePurpose | null>(null);
  useEffect(() => { if (q.data?.policy) setPolicy(q.data.policy); }, [q.data?.policy]);
  const dirty = policy && q.data?.policy && JSON.stringify({ ...policy, updatedAt: '' }) !== JSON.stringify({ ...q.data.policy, updatedAt: '' });
  const storage = items.filter((l) => l.purpose === 'storage');

  async function save() {
    if (!policy) return;
    setSaving(true);
    try {
      await api('/api/v1/storage-policy', { method: 'PUT', json: policy });
      await qc.invalidateQueries({ queryKey: queryKeys.storage() });
      toast('Storage tiers saved');
    } catch (err) {
      toast(errorDetail(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <h2>Storage tiers</h2>
      <p className="lede">Where PRISM keeps each kind of file belonging to assets. Tiers are storage only: files come in through uploads and watch folders, never by scanning a tier. Existing files stay where they are; move them with a transfer from the asset’s Storage tab, or many at once with a migration (Migrations, administrators).</p>
      {policy && (
        <div className="panel-card" style={{ maxWidth: 980 }} data-testid="storage-policy">
          <div className="field-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 14 }}>
            {TIERS.map((t) => {
              const options = items.filter((l) => l.purpose === 'storage' || (t.role === 'exports' && l.purpose === 'export'));
              return (
                <div className="field" key={t.key}>
                  <label htmlFor={t.key}>{t.label}</label>
                  <select id={t.key} className="select" value={policy[t.key]} onChange={(e) => setPolicy({ ...policy, [t.key]: e.target.value })} data-testid={`tier-${t.key}`}>
                    {options.map((l) => {
                      const why = l.eligibility[t.role];
                      return <option key={l.id} value={l.id} disabled={Boolean(why) && l.id !== policy[t.key]} title={why ?? undefined}>
                        {l.name} · {l.site}{why ? ` (${why})` : ''}
                      </option>;
                    })}
                  </select>
                  <span className="help">{t.help}</span>
                </div>
              );
            })}
          </div>
          <div className="field">
            <label>When an agent discovers content that is already in the library</label>
            <Segmented role="radiogroup" value={policy.discoveryDuplicates} onChange={(discoveryDuplicates) => setPolicy({ ...policy, discoveryDuplicates })} options={[
              { value: 'link', label: 'Catalogue it, linked as a duplicate' }, { value: 'skip', label: 'Skip it' },
            ]} />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn primary" disabled={!dirty || saving} onClick={() => void save()} data-testid="save-tiers"><Save size={14} /> Save tiers</button>
            <span className="faint" style={{ fontSize: 12 }}>Last changed {relative(q.data?.policy?.updatedAt)}</span>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 32 }}>
        <h2 style={{ margin: 0 }}>Storage locations</h2>
        <span style={{ flex: 1 }} />
        <button className="btn" onClick={() => setAdding('storage')} data-testid="add-location"><Plus size={14} /> Add storage</button>
      </div>
      <p className="lede">Server folders, S3 and OCI buckets, and folders on storage agents that can hold a storage tier. Availability is checked continuously; an offline location keeps its assets visible.</p>
      <div className="cards" data-testid="storage-locations">
        {storage.map((l) => <LocationCard key={l.id} loc={l} all={items} />)}
      </div>

      <IntegrityCheck />

      {adding && <AddLocationDialog purpose={adding} onClose={() => setAdding(null)} />}
    </>
  );
}
